import { messaging, getToken, deleteToken } from '../firebase';
import { API_BASE_URL } from '../config/apiConfig';

/**
 * Checks if the user is already subscribed to notifications.
 * @returns {boolean} true if subscribed, false otherwise
 */
export async function isUserSubscribed() {
  if (Notification.permission !== 'granted') return false;

  try {
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
    });

    if (!token) return false;

    const response = await fetch(`${API_BASE_URL}/api/check-fcm-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });

    const { exists } = await response.json();
    return exists;
  } catch (err) {
    console.error('Subscription status check failed:', err);
    return false;
  }
}

/**
 * Subscribes the user to notifications.
 * Prevents duplicate token storage by checking first.
 * @param {string|null} userId - Optional user ID
 */
export async function subscribeToNotifications(userId = null) {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      alert('Notification permission denied.');
      return;
    }

    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
    });

    if (!token) throw new Error('FCM token not retrieved.');
    console.log('FCM Token:', token);

    // Check if already subscribed
    const checkRes = await fetch(`${API_BASE_URL}/api/check-fcm-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });

    const { exists } = await checkRes.json();
    if (exists) {
      console.log('Already subscribed. Skipping save.');
      return;
    }

    const deviceInfo = `${navigator.platform} - ${navigator.userAgent}`;

    await fetch(`${API_BASE_URL}/api/save-fcm-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, deviceInfo, userId })
    });

    alert('Subscribed to job notifications!');
  } catch (error) {
    console.error('Subscription failed:', error);
    alert('Failed to subscribe to notifications.');
  }
}

/**
 * Unsubscribes the user from notifications.
 * Deletes token from Firebase and marks it inactive in backend.
 */
export async function unsubscribeFromNotifications() {
  try {
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
    });

    if (!token) {
      alert('No subscription found.');
      return;
    }

    // Delete token from Firebase
    await deleteToken(messaging);

    // Mark token inactive in backend
    await fetch(`${API_BASE_URL}/api/remove-fcm-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });

    alert('Unsubscribed from job notifications!');
  } catch (error) {
    console.error('Unsubscription failed:', error);
    alert('Failed to unsubscribe from notifications.');
  }
}
